import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Save, RotateCcw, Pencil, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const fmt = (v) => v == null || isNaN(v) ? "—" : Number(v).toFixed(2);

function bucketKey(b) { return `${b.type}|${b.vintage}|${b.bracket}`; }

function EditDialog({ open, onOpenChange, bucket, lineItems, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (bucket) {
      const start = {};
      lineItems.forEach(li => { start[li] = bucket.items?.[li] ?? 0; });
      setDraft(start);
    }
  }, [bucket, lineItems]);

  if (!bucket) return null;

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const total = lineItems.reduce((s, li) => s + Number(draft[li] || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const items = {};
      lineItems.forEach(li => { items[li] = Number(draft[li]) || 0; });
      await api.put("/admin/cost-matrix", {
        type: bucket.type, vintage: bucket.vintage, bracket: bucket.bracket, items,
      });
      toast.success(`Saved ${bucket.type} · ${bucket.vintage} · ${bucket.bracket} beds`);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete("/admin/cost-matrix", {
        params: { type: bucket.type, vintage: bucket.vintage, bracket: bucket.bracket },
      });
      toast.success("Reset to default");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-2 border-brand-ink rounded-none" data-testid="cm-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl tracking-tight">
            {bucket.type} · {bucket.vintage} · {bucket.bracket} beds
          </DialogTitle>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            Per-bed-per-day costs (₹) {bucket.is_override && <span className="ml-2 text-brand-pink">· OVERRIDDEN</span>}
          </div>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {lineItems.map(li => (
            <div key={li}>
              <Label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 truncate block">{li}</Label>
              <Input
                type="number"
                step="0.01"
                value={draft[li] ?? 0}
                onChange={e => set(li, e.target.value)}
                className="rounded-none"
                data-testid={`cm-input-${li.replace(/[^a-zA-Z0-9]/g, "-")}`}
              />
            </div>
          ))}
        </div>
        <div className="bg-brand-paper border border-brand-line p-3 mt-2 flex items-center justify-between">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total ₹/bed/day</div>
          <div className="font-display font-black text-2xl text-brand-ink">{total.toFixed(2)}</div>
        </div>
        <DialogFooter className="flex gap-2">
          {bucket.is_override && (
            <Button onClick={reset} variant="outline" disabled={saving} className="rounded-none border-2 border-brand-pink text-brand-pink hover:bg-brand-pink hover:text-white" data-testid="cm-reset-bucket">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset to default
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} variant="outline" disabled={saving} className="rounded-none">
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-none bg-brand-ink text-brand-yellow hover:bg-brand-yellow hover:text-brand-ink border-2 border-brand-ink" data-testid="cm-save-bucket">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetAllDialog({ open, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cost-matrix/reset-all");
      toast.success(`Reset ${data.deleted} override(s)`);
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-2 border-brand-pink rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl">Reset all overrides?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-700">This deletes every admin-saved override and brings the cost matrix back to the file defaults. This cannot be undone.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-none">Cancel</Button>
          <Button onClick={run} disabled={busy} className="rounded-none bg-brand-pink text-white hover:bg-brand-ink" data-testid="cm-reset-all-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />} Reset all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CostMatrixPanel() {
  const [data, setData] = useState({ buckets: [], line_items: [], types: [], vintages: [], brackets: [], overrides_count: 0 });
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterVintage, setFilterVintage] = useState("all");
  const [editing, setEditing] = useState(null);
  const [resetAllOpen, setResetAllOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cost-matrix");
      setData(data);
    } catch (e) {
      toast.error("Failed to load cost matrix");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return data.buckets.filter(b =>
      (filterType === "all" || b.type === filterType) &&
      (filterVintage === "all" || b.vintage === filterVintage)
    );
  }, [data.buckets, filterType, filterVintage]);

  return (
    <div className="space-y-4" data-testid="cost-matrix-panel">
      <div className="bg-white border border-brand-line p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Cost matrix · per-bed-per-day benchmarks</div>
            <h2 className="font-display font-black text-xl tracking-tight mt-1">
              {data.buckets.length} buckets
              {data.overrides_count > 0 && (
                <span className="ml-3 font-mono-tech text-[11px] uppercase px-2 py-1 rounded bg-brand-pink/20 text-brand-pink" data-testid="cm-overrides-badge">
                  {data.overrides_count} overridden
                </span>
              )}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="rounded-none w-44" data-testid="cm-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {data.types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterVintage} onValueChange={setFilterVintage}>
              <SelectTrigger className="rounded-none w-32" data-testid="cm-filter-vintage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vintage</SelectItem>
                {data.vintages.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              onClick={() => setResetAllOpen(true)}
              variant="outline"
              disabled={loading || data.overrides_count === 0}
              className="rounded-none border-2 border-brand-pink text-brand-pink hover:bg-brand-pink hover:text-white"
              data-testid="cm-reset-all"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Reset all
            </Button>
          </div>
        </div>
        <p className="text-xs text-zinc-600 leading-relaxed">
          Edit per-bed-per-day cost benchmarks per (Type × Vintage × Bed bracket). Overrides apply instantly to every Property IC calculation.
          File-default values are read-only here — saving creates an override; "Reset to default" deletes it.
        </p>
      </div>

      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm" data-testid="cm-table">
          <thead className="bg-brand-paper border-b border-brand-line">
            <tr>
              <th className="text-left px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">Type</th>
              <th className="text-left px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">Vintage</th>
              <th className="text-left px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">Bracket</th>
              {data.line_items.slice(0, 6).map(li => (
                <th key={li} className="text-right px-2 py-2 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-zinc-500 truncate max-w-[100px]" title={li}>{li}</th>
              ))}
              <th className="text-right px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total ₹/bed/day</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b, i) => {
              const total = data.line_items.reduce((s, li) => s + Number(b.items?.[li] || 0), 0);
              return (
                <tr key={bucketKey(b)} className={`border-b border-brand-line ${i % 2 ? "bg-brand-paper/30" : ""} ${b.is_override ? "border-l-4 border-l-brand-pink" : ""}`} data-testid={`cm-row-${b.type}-${b.vintage}-${b.bracket}`}>
                  <td className="px-3 py-2 font-display font-bold text-sm">{b.type}</td>
                  <td className="px-3 py-2 font-mono-tech text-[11px] uppercase">{b.vintage}</td>
                  <td className="px-3 py-2 font-mono-tech text-[11px]">{b.bracket} beds</td>
                  {data.line_items.slice(0, 6).map(li => (
                    <td key={li} className="text-right px-2 py-2 font-mono-tech text-[11px]">{fmt(b.items?.[li])}</td>
                  ))}
                  <td className="text-right px-3 py-2 font-display font-bold text-sm">{total.toFixed(2)}</td>
                  <td className="text-right px-3 py-2">
                    <button
                      onClick={() => setEditing(b)}
                      className="font-mono-tech text-[10px] uppercase tracking-wide bg-brand-ink text-white px-2 py-1 hover:bg-brand-yellow hover:text-brand-ink rounded inline-flex items-center gap-1"
                      data-testid={`cm-edit-${b.type}-${b.vintage}-${b.bracket}`}
                    >
                      <Pencil className="w-3 h-3" /> Edit all 14
                    </button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && !loading && (
              <tr><td colSpan={10} className="text-center py-8 font-mono-tech text-[11px] uppercase tracking-wide text-zinc-400">No buckets match filter</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <EditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} bucket={editing} lineItems={data.line_items} onSaved={load} />
      <ResetAllDialog open={resetAllOpen} onOpenChange={setResetAllOpen} onDone={load} />
    </div>
  );
}
